import type { AssetYearLaidBreakDown } from '@Types/failureGrade.types';

// Years outside this window are placeholder dates (a 0001-01-01 default, a
// 9999-12-31 sentinel), not real installation years. The bounds are generous on
// purpose: the aim is to keep a placeholder from stretching the axis across
// millennia, not to judge whether an old or planned asset is plausible.
const MIN_VALID_YEAR = 1700;
const MAX_VALID_YEAR = 2200;

export interface YearPoint {
	year: number;
	count: number;
	// True for a year with no assets, added only to hold the line at zero across a
	// gap. Stated explicitly so callers need not infer it from a zero count.
	filled: boolean;
}

export interface YearSeries {
	// One point per year from the first valid year to the last.
	points: YearPoint[];
	// Assets with no valid year - the server's "Unknown" bucket and any placeholder
	// year outside the valid window - kept off the axis for the caller to report.
	unknownCount: number;
}

const isValidYear = (key: string, year: number): boolean =>
	key.trim() !== '' && Number.isInteger(year) && year >= MIN_VALID_YEAR && year <= MAX_VALID_YEAR;

/**
 * Build the installation-year line from the server's year buckets.
 *
 * The chart used to plot bucket keys as categories, which spaced 1988, 1989 and
 * 2005 evenly and misrepresented the distribution. This returns points for a
 * numeric axis instead, filling years that have no assets with zero so a gap
 * reads as "none installed" rather than being bridged by a straight line.
 *
 * A bucket with no valid year has no position on a year axis, so it is excluded
 * from the points and its asset count returned for the caller to show, rather
 * than being silently dropped.
 */
export const yearSeriesForDisplay = (rawData: AssetYearLaidBreakDown): YearSeries => {
	const countsByYear = new Map<number, number>();
	let unknownCount = 0;

	Object.entries(rawData).forEach(([key, bucket]) => {
		const count = bucket?.count || 0;
		const year = Number(key);
		if (isValidYear(key, year)) {
			countsByYear.set(year, (countsByYear.get(year) ?? 0) + count);
		} else {
			unknownCount += count;
		}
	});

	if (countsByYear.size === 0) return { points: [], unknownCount };

	const years = Array.from(countsByYear.keys());
	const firstYear = Math.min(...years);
	const lastYear = Math.max(...years);

	const points: YearPoint[] = [];
	for (let year = firstYear; year <= lastYear; year++) {
		const count = countsByYear.get(year);
		points.push(count === undefined ? { year, count: 0, filled: true } : { year, count, filled: false });
	}
	return { points, unknownCount };
};
